import { useMemo } from "react";
import { Table, TableColumnType, Spin, EmptyState, Icons, Icon, Button, Division, Tooltip, Flex } from "@ir/ui";
import useAddChargebreakdown from "./hooks/useAddChargebreakdown";
import { AddChargebreakdownItem } from "./class/AddChargebreakdownItem";
import { ShowPreviewCell } from "./utils/AddChargebreakdownUtils";
import { IAddChargebreakdownCsvDataAfterMapping } from "./interface/AddChargebreakdownInterface";

const DeleteChargebreakdownButton = ({ chargebreakdownKey }: { chargebreakdownKey: string }) => {
  const { deleteChargebreakdownFromFinalChargebreakdownData, isSubmitting, isSubmitted } = useAddChargebreakdown();

  return (
    <Division className="flex justify-center">
      <Tooltip content="Remove" placement="left">
        <Button
          size="small"
          color="grey"
          variant="clear"
          className="p-0"
          onPress={() => deleteChargebreakdownFromFinalChargebreakdownData(chargebreakdownKey)}
          isDisabled={isSubmitting || isSubmitted}
        >
          <Icon name={Icons.Close} />
        </Button>
      </Tooltip>
    </Division>
  );
};

const getColumns = (finalChargebreakdownData: AddChargebreakdownItem[]): TableColumnType<AddChargebreakdownItem>[] => {
  const columns: TableColumnType<AddChargebreakdownItem>[] = [
    {
      title: "UPC",
      dataIndex: "upcItemNumber",
      key: "upcItemNumber",
      fixed: "left",
      width: 150,
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.upcItemNumberErrors?.length ? record.originalChargebreakdownData.upcItemNumber : record.upcItemNumber}
          errors={record.upcItemNumberErrors}
        />
      ),
    },
    {
      title: "Item Number",
      dataIndex: "itemNumber",
      key: "itemNumber",
      fixed: "left",
      width: 150,
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.itemNumberErrors?.length ? record.originalChargebreakdownData.itemNumber : record.itemNumber}
          errors={record.itemNumberErrors}
        />
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      fixed: "left",
      width: 100,
      align: "right",
      cellRender: (_, record) => (
        <Flex justifyContent="end">
          <ShowPreviewCell
            value={record.totalAmountErrors?.length ? record.originalChargebreakdownData.totalAmount : record.totalAmountFormatted}
            errors={record.totalAmountErrors}
          />
        </Flex>
      ),
    },
    {
      title: "Item Description",
      dataIndex: "itemDescription",
      key: "itemDescription",
      width: 120,
      align: "left",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.itemDescriptionErrors?.length ? record.originalChargebreakdownData.itemDescription : record.itemDescription}
          errors={record.itemDescriptionErrors}
        />
      ),
    },
    {
      title: "Distribution Center",
      dataIndex: "distributionCenter",
      key: "distributionCenter",
      width: 120,
      align: "left",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.distributionCenterErrors?.length ? record.originalChargebreakdownData.distributionCenter : record.distributionCenter}
          errors={record.distributionCenterErrors}
        />
      ),
    },
    {
      title: "Order Quantity",
      dataIndex: "orderQuantity",
      key: "orderQuantity",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.orderQuantityErrors?.length ? record.originalChargebreakdownData.orderQuantity : record.orderQuantityFormatted}
          errors={record.orderQuantityErrors}
        />
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.quantityErrors?.length ? record.originalChargebreakdownData.quantity : record.quantityFormatted}
          errors={record.quantityErrors}
        />
      ),
    },
    {
      title: "Quantity Difference",
      dataIndex: "quantityDifference",
      key: "quantityDifference",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.quantityDifferenceErrors?.length ? record.originalChargebreakdownData.quantityDifference : record.quantityDifferenceFormatted}
          errors={record.quantityDifferenceErrors}
        />
      ),
    },
    {
      title: "Pack Size",
      dataIndex: "packSize",
      key: "packSize",
      width: 120,
      cellRender: (_, record) => (
        <ShowPreviewCell value={record.packSizeErrors?.length ? record.originalChargebreakdownData.packSize : record.packSize} errors={record.packSizeErrors} />
      ),
    },
    {
      title: "Unit Cost",
      dataIndex: "unitCost",
      key: "unitCost",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.unitCostErrors?.length ? record.originalChargebreakdownData.unitCost : record.unitCostFormatted}
          errors={record.unitCostErrors}
        />
      ),
    },
    {
      title: "Received Unit Cost",
      dataIndex: "receivedUnitCost",
      key: "receivedUnitCost",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.receivedUnitCostErrors?.length ? record.originalChargebreakdownData.receivedUnitCost : record.receivedUnitCostFormatted}
          errors={record.receivedUnitCostErrors}
        />
      ),
    },
    {
      title: "Cost Difference",
      dataIndex: "costDifference",
      key: "costDifference",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.costDifferenceErrors?.length ? record.originalChargebreakdownData.costDifference : record.costDifferenceFormatted}
          errors={record.costDifferenceErrors}
        />
      ),
    },
    {
      title: "Allowance",
      dataIndex: "allowance",
      key: "allowance",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.allowanceErrors?.length ? record.originalChargebreakdownData.allowance : record.allowance}
          errors={record.allowanceErrors}
        />
      ),
    },
    {
      title: "Received Date",
      dataIndex: "receivedDate",
      key: "receivedDate",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.receivedDateErrors?.length ? record.originalChargebreakdownData.receivedDate : record.receivedDateFormatted}
          errors={record.receivedDateErrors}
        />
      ),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.expiryDateErrors?.length ? record.originalChargebreakdownData.expiryDate : record.expiryDateFormatted}
          errors={record.expiryDateErrors}
        />
      ),
    },
    {
      title: "Ship Date",
      dataIndex: "shipDate",
      key: "shipDate",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.shipDateErrors?.length ? record.originalChargebreakdownData.shipDate : record.shipDateFormatted}
          errors={record.shipDateErrors}
        />
      ),
    },
    {
      title: "PO Number",
      dataIndex: "poNumber",
      key: "poNumber",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell value={record.poNumberErrors?.length ? record.originalChargebreakdownData.poNumber : record.poNumber} errors={record.poNumberErrors} />
      ),
    },
    {
      title: "PO Date",
      dataIndex: "poDate",
      key: "poDate",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.poDateErrors?.length ? record.originalChargebreakdownData.poDate : record.poDateFormatted}
          errors={record.poDateErrors}
        />
      ),
    },
    {
      title: "Retailer Name",
      dataIndex: "retailerName",
      key: "retailerName",
      width: 120,
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.retailerNameErrors?.length ? record.originalChargebreakdownData.retailerName : record.retailerName}
          errors={record.retailerNameErrors}
        />
      ),
    },
    {
      title: "Retailer Number",
      dataIndex: "retailerNumber",
      key: "retailerNumber",
      width: 120,
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.retailerNumberErrors?.length ? record.originalChargebreakdownData.retailerNumber : record.retailerNumber}
          errors={record.retailerNumberErrors}
        />
      ),
    },
    {
      title: "Ref. Invoice",
      dataIndex: "refInvoice",
      key: "refInvoice",
      width: 200,
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.refInvoiceErrors?.length ? record.originalChargebreakdownData.refInvoice : record.refInvoice}
          errors={record.refInvoiceErrors}
        />
      ),
    },
    {
      title: "Invoice Amount",
      dataIndex: "invoiceAmount",
      key: "invoiceAmount",
      width: 150,
      align: "right",
      cellRender: (_, record) => {
        return (
          <ShowPreviewCell
            value={record.invoiceAmountErrors?.length ? record.originalChargebreakdownData.invoiceAmount : record.invoiceAmountFormatted}
            errors={record.invoiceAmountErrors}
          />
        );
      },
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.invoiceDateErrors?.length ? record.originalChargebreakdownData.invoiceDate : record.invoiceDateFormatted}
          errors={record.invoiceDateErrors}
        />
      ),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      width: 120,
      align: "right",
      cellRender: (_, record) => (
        <ShowPreviewCell value={record.discountErrors?.length ? record.originalChargebreakdownData.discount : record.discount} errors={record.discountErrors} />
      ),
    },
    {
      title: "Division Name",
      dataIndex: "divisionName",
      key: "divisionName",
      width: 120,
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.divisionNameErrors?.length ? record.originalChargebreakdownData.divisionName : record.divisionName}
          errors={record.divisionNameErrors}
        />
      ),
    },
    {
      title: "Division Number",
      dataIndex: "divisionNumber",
      key: "divisionNumber",
      width: 120,
      cellRender: (_, record) => (
        <ShowPreviewCell
          value={record.divisionNumberErrors?.length ? record.originalChargebreakdownData.divisionNumber : record.divisionNumber}
          errors={record.divisionNumberErrors}
        />
      ),
    },
  ];

  const rawChargebreakdownData = finalChargebreakdownData.map(each => each.originalChargebreakdownData);

  const finalFilteredColumns = columns.filter(column => {
    if (column.key === "totalAmount") {
      return true;
    }
    const hasData = rawChargebreakdownData.some(item => item[column.key as keyof IAddChargebreakdownCsvDataAfterMapping]);
    return hasData;
  });

  return [
    ...finalFilteredColumns,
    {
      title: "Delete",
      key: "delete",
      dataIndex: "key",
      align: "center",
      fixed: "right",
      width: 60,
      cellRender: value => <DeleteChargebreakdownButton chargebreakdownKey={value as string} />,
    },
  ];
};

const AddChargebreakdownVerify = () => {
  const { finalChargebreakdownData, isProcessingSuccessResponse, isSubmitted, currentStep, modalHeaderHeight, modalFooterHeight } = useAddChargebreakdown();

  const columns = useMemo(() => getColumns(finalChargebreakdownData ?? []), [finalChargebreakdownData]);

  // Calculate the container height
  const windowHeight = globalThis.innerHeight;
  const containerHeight = useMemo(() => {
    return windowHeight - modalHeaderHeight - modalFooterHeight - 60;
  }, [windowHeight, modalHeaderHeight, modalFooterHeight]);

  if (isSubmitted && currentStep === "verify-chargebreakdown") {
    // Show success message if no error checks
    return (
      <EmptyState
        className="p-6"
        title="All charge breakdowns processed"
        description="You can click the close button and view the charge breakdowns."
        icon={Icons.CheckCircle}
      />
    );
  }

  if (!finalChargebreakdownData || finalChargebreakdownData.length === 0) {
    return (
      <EmptyState
        className="p-6"
        title="No charge breakdown data found"
        description={"All charge breakdown data has been removed. Please go back and re-upload to continue."}
        icon={Icons.File}
      />
    );
  }

  return (
    <Spin spinning={isProcessingSuccessResponse} tip="Processing...">
      <Table
        columns={columns}
        dataSource={finalChargebreakdownData ?? []}
        rowHoverable={false}
        sticky={true}
        border="row"
        scroll={{ x: "100%", y: containerHeight }}
        className="add-manual-chargebreakdown-table"
        aria-label="Add charge breakdown table"
      />
    </Spin>
  );
};

export default AddChargebreakdownVerify;
